import {
  Fragment,
  MouseEvent,
  MouseEventHandler,
  PointerEvent,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useHistory } from 'react-router';
import { NavLink, Route, useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import useSWR from 'swr';

import APIMethodKeys from '../../../../client/APIMethodKeys';
import { stringifyQuery } from '../../../../utils';
import isEmpty from '../../../../utils/isEmpty';
import { formatPath } from '../../../../utils/path';
import Avatar from '../../../common/base/Avatar';
import Badge from '../../../common/base/Badge';
import { ClickableArea } from '../../../common/base/Button';
import Divider from '../../../common/base/Divider';
import Icon, { IconName, StyledIcon } from '../../../common/base/Icon';
import Link from '../../../common/base/Link';
import Text from '../../../common/base/Text';
import Tooltip from '../../../common/base/Tooltip';
import Dropdown from '../../../common/Dropdown';
import { Div } from '../../../common/helpers/StyledUtils';
import Search from '../../../common/Search';
import { GlobalContext } from '../../../contexts/GlobalContext';
import useLocalStorage from '../../../hooks/useLocalStorage';
import useSearchQuery from '../../../hooks/useSearchQuery';
import { DashboardContext } from '../DashboardContext';
import { DateRange } from '../Metrics/MetricDatePicker';
import { ViewsContext, ViewsContextProvider } from '../ViewsContext';
import CollapsedNavIcon from './CollapsedNavIcon';
import NavFooter from './NavFooter';
import TeamSelection from './TeamSelection/TeamSelection';
import DropdownMenu from '../../../common/DropdownMenu';
import { DASHBOARD_NAV_COLLAPSED_WIDTH } from '../../../layouts/ViewLayout';

const StyledDashboardNav = styled.div<{ collapsed: boolean }>(({ theme, collapsed }) => {
  const { is_degraded_banner_active } = useContext(DashboardContext);
  const banner_height = '32px';
  return css`
    display: flex;
    flex-direction: column;
    position: sticky;
    top: ${is_degraded_banner_active ? banner_height : 0};
    max-height: ${is_degraded_banner_active ? `calc(100vh - ${banner_height})` : '100vh'};
    min-height: ${is_degraded_banner_active ? `calc(100vh - ${banner_height})` : '100vh'};
    background-color: ${theme.colors.surface.base.background};
    border-right: ${theme.border};
    z-index: ${theme.zindex.sticky + 2};
    width: ${collapsed ? theme.pxToRem(DASHBOARD_NAV_COLLAPSED_WIDTH) : 'auto'};
  `;
});

const StyledNavResizeHandle = styled.div<{ $highlighted: boolean }>(
  ({ theme, $highlighted }) => css`
    position: absolute;
    top: 0;
    right: ${theme.pxToRem(-4)};
    bottom: 0;
    width: ${theme.pxToRem(8)};
    cursor: ${$highlighted ? 'ew-resize' : 'default'};
    z-index: ${theme.zindex.fixed + 1};
    opacity: ${$highlighted ? 1 : 0};

    &::after {
      content: '';
      position: absolute;
      top: 0;
      right: ${theme.pxToRem(3)};
      bottom: 0;
      width: ${theme.pxToRem(2)};
      background-color: ${theme.colors.outline.focus.primary};
    }

    &:focus-visible::after {
      opacity: 1;
    }

    &:focus-visible {
      outline: none;
    }
  `,
);

const NAV_COLLAPSE_DRAG_THRESHOLD = 24;
const NAV_RESIZE_HANDLE_HOVER_DELAY = 200;

export const DashboardNavShell = ({ children }: { children: ReactNode }) => {
  const { nav_collapsed, setNavCollapsed } = useContext(DashboardContext);
  const nav_border_drag_start_x = useRef<number | null>(null);
  const nav_collapsed_ref = useRef(nav_collapsed);
  const nav_border_hover_timeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [nav_border_dragging, setNavBorderDragging] = useState(false);
  const [nav_border_hovered, setNavBorderHovered] = useState(false);
  nav_collapsed_ref.current = nav_collapsed;

  useEffect(
    () => () => {
      if (nav_border_hover_timeout.current) {
        clearTimeout(nav_border_hover_timeout.current);
      }
    },
    [],
  );

  const handleNavBorderPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) {
      return;
    }

    if (nav_border_hover_timeout.current) {
      clearTimeout(nav_border_hover_timeout.current);
      nav_border_hover_timeout.current = undefined;
    }
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    nav_border_drag_start_x.current = e.clientX;
    setNavBorderDragging(true);
  };

  const handleNavBorderPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (nav_border_drag_start_x.current === null) {
      return;
    }

    const delta_x = e.clientX - nav_border_drag_start_x.current;
    if (Math.abs(delta_x) < NAV_COLLAPSE_DRAG_THRESHOLD) {
      return;
    }

    const should_collapse = delta_x < 0;
    if (should_collapse !== nav_collapsed_ref.current) {
      nav_collapsed_ref.current = should_collapse;
      setNavCollapsed(should_collapse);
    }
    nav_border_drag_start_x.current = e.clientX;
  };

  const handleNavBorderPointerEnd = (e: PointerEvent<HTMLDivElement>) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    nav_border_drag_start_x.current = null;
    setNavBorderDragging(false);
  };

  const handleNavBorderPointerEnter = () => {
    if (nav_border_dragging || nav_border_hover_timeout.current) {
      return;
    }

    nav_border_hover_timeout.current = setTimeout(() => {
      nav_border_hover_timeout.current = undefined;
      setNavBorderHovered(true);
    }, NAV_RESIZE_HANDLE_HOVER_DELAY);
  };

  const handleNavBorderPointerLeave = () => {
    if (nav_border_hover_timeout.current) {
      clearTimeout(nav_border_hover_timeout.current);
      nav_border_hover_timeout.current = undefined;
    }
    setNavBorderHovered(false);
  };

  return (
    <StyledDashboardNav collapsed={nav_collapsed}>
      <StyledNavResizeHandle
        role="separator"
        aria-label={nav_collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        aria-orientation="vertical"
        tabIndex={0}
        $highlighted={nav_border_dragging || nav_border_hovered}
        onPointerEnter={handleNavBorderPointerEnter}
        onPointerLeave={handleNavBorderPointerLeave}
        onPointerDown={handleNavBorderPointerDown}
        onPointerMove={handleNavBorderPointerMove}
        onPointerUp={handleNavBorderPointerEnd}
        onPointerCancel={handleNavBorderPointerEnd}
      />
      {children}
    </StyledDashboardNav>
  );
};

export const ScrollContainer = styled(Div)`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

export const StyledNavLinks = styled.div<{ $hover_background?: 'neutral' | 'neutral_variant' }>(
  ({ theme, $hover_background = 'neutral_variant' }) => css`
    display: flex;
    gap: 2px;
    flex-direction: column;

    > a,
    > button {
      display: flex;
      align-items: center;
      gap: ${theme.spacing(2)};
      position: relative;
      text-decoration: none;
      padding: ${theme.spacing(1)};
      border-radius: ${theme.radius.normal};

      color: ${theme.colors.on.neutral.primary_neutral};
      ${StyledIcon} {
        color: ${theme.colors.on.neutral.tertiary_neutral};
        fill: ${theme.colors.on.neutral.tertiary_neutral};
        margin-left: 0;
        margin-right: 0;
      }

      &.active {
        background-color: ${$hover_background === 'neutral'
          ? theme.colors.surface.base.hover.neutral
          : theme.colors.surface.base.hover.neutral_variant};
        color: ${theme.colors.on.neutral.primary_neutral};
      }

      &:hover {
        background-color: ${$hover_background === 'neutral'
          ? theme.colors.surface.base.hover.neutral
          : theme.colors.surface.base.hover.neutral_variant};
      }

      &:focus-visible {
        outline: none;
        &::before {
          content: '';
          position: absolute;
          top: -3px;
          left: -3px;
          right: -3px;
          bottom: -3px;
          border: 2px solid ${theme.colors.outline.focus.primary};
          border-radius: ${theme.radius.large};
        }
      }
    }
  `,
);

export const StyledViewsContainer = styled(Div)(
  ({ theme }) => css`
    margin-right: -4px;
    padding-right: 4px;
    margin-bottom: -4px;
    padding-bottom: 4px;
    position: relative;

    &::before {
      content: '';
      position: absolute;
      left: ${theme.pxToRem(14)};
      top: ${theme.pxToRem(8)};
      bottom: ${theme.pxToRem(8)};
      background-color: ${theme.colors.outline.neutral};
      width: 1px;
    }

    ${StyledNavLinks} {
      > a:not(.active) {
        color: ${theme.colors.on.neutral.tertiary_neutral};
      }
    }
  `,
);

const StyledCompactViewsScrollContainer = styled(Div)`
  overflow-y: auto;

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

export const StyledCollapsedLinkWrapper = styled.div(
  ({ theme }) => css`
    position: relative;

    > a,
    > button {
      display: flex;
      align-items: center;
      gap: ${theme.spacing(2)};
      position: relative;
      text-decoration: none;
      padding: ${theme.spacing(1)};
      border-radius: ${theme.radius.normal};

      color: ${theme.colors.on.neutral.primary_neutral};
      ${StyledIcon} {
        color: ${theme.colors.on.neutral.tertiary_neutral};
        fill: ${theme.colors.on.neutral.tertiary_neutral};
        margin-left: 0;
        margin-right: 0;
      }

      &:hover {
        background-color: ${theme.colors.surface.base.hover.neutral_variant};
      }

      &.active {
        background-color: ${theme.colors.surface.base.hover.neutral_variant};
        color: ${theme.colors.on.neutral.primary_neutral};
      }

      &:focus-visible {
        outline: none;
        &::before {
          content: '';
          position: absolute;
          top: -3px;
          left: -3px;
          right: -3px;
          bottom: -3px;
          border: 2px solid ${theme.colors.outline.focus.primary};
          border-radius: ${theme.radius.large};
        }
      }
    }
  `,
);

const StyledActivityIndicator = styled(Div)<{ active: boolean }>(
  ({ theme, active }) => css`
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: ${active ? theme.colors.on.neutral.success : theme.colors.on.neutral.danger};
    border: 4px solid
      ${active ? theme.colors.surface.container.success : theme.colors.surface.container.danger};
    position: relative;
    z-index: 0;
    margin-left: 0 ${theme.spacing(1.5)};
  `,
);

const inspect_sections = [
  {
    key: 'requests',
    path: `/requests`,
    label: 'Requests',
    icon: 'requests' as const,
  },
  {
    key: 'events',
    path: `/events`,
    label: 'Events',
    icon: 'events' as const,
  },
  {
    key: 'bookmarks',
    path: `/bookmarks`,
    label: 'Bookmarks',
    icon: 'bookmarks' as const,
  },
];

const configure_sections: { path: string; label: string; icon: IconName }[] = [
  {
    path: '/connections',
    label: 'Connections',
    icon: 'connections',
  },
  {
    path: '/sources',
    label: 'Sources',
    icon: 'source',
  },
  {
    path: '/destinations',
    label: 'Destinations',
    icon: 'destinations',
  },
  {
    path: '/transformations',
    label: 'Transformations',
    icon: 'transformation',
  },
];

export const CollapsedNavLink = ({
  to,
  label,
  icon,
  isActive,
  children,
}: {
  to: string;
  label: string;
  icon: IconName;
  isActive?: () => boolean;
  children?: ReactNode;
}) => {
  return (
    <StyledCollapsedLinkWrapper>
      <NavLink to={to} isActive={isActive} aria-label={label}>
        <Icon left icon={icon} pointer p={0.5} />
      </NavLink>
      {children}
    </StyledCollapsedLinkWrapper>
  );
};

const Views = ({
  type,
  compact = false,
  include_all = false,
  toggle,
}: {
  type: 'events' | 'requests';
  compact?: boolean;
  include_all?: boolean;
  toggle?: (show: boolean) => void;
}) => {
  const { views } = useContext(ViewsContext);
  const { pathname } = useLocation();
  const [view_search, setViewSearch] = useState('');

  const { active_cli_sessions } = useContext(DashboardContext);
  const is_cli_active = active_cli_sessions && active_cli_sessions.length > 0;

  const all_views = views.models.sort((a, b) => {
    if (a.id === 'cli') return -1;
    if (b.id === 'cli') return 1;
    return a.name.localeCompare(b.name);
  });

  const displayed_views = views.models.filter(
    (view, i) =>
      (i < 5 && (view.type === type || (view.id === 'cli' && pathname === '/requests/cli'))) ||
      pathname ===
        `/${type}/${formatPath(view.name, {
          escape_parentheses: false,
        })}`,
  );

  const getViewPath = (view: (typeof all_views)[number]) =>
    `/${type}/${formatPath(view.name, {
      escape_parentheses: false,
    })}`;
  const filtered_views = view_search
    ? all_views.filter((view) => view.name.toLowerCase().includes(view_search.toLowerCase()))
    : all_views;

  if (compact) {
    return (
      <>
        <Search
          borderless
          small
          placeholder={`Find a ${type} view...`}
          value={view_search}
          onChange={setViewSearch}
        />
        <Divider />
        <StyledCompactViewsScrollContainer max_h={{ px: 242 }} p={{ x: 1, b: 1, t: 1 }}>
          <StyledNavLinks $hover_background="neutral">
            {filtered_views.map((view) => (
              <NavLink
                key={view.id}
                to={getViewPath(view)}
                isActive={() => pathname === getViewPath(view)}
                onClick={() => toggle?.(false)}>
                <Div p={{ x: 1 }} w={100} flex={{ align: 'center', justify: 'space-between' }}>
                  <Text semi ellipsis>
                    {view.name}
                  </Text>
                  {view?.id === 'cli' && (
                    <Tooltip
                      align="right"
                      offset={[-8, 12]}
                      tooltip={is_cli_active ? 'CLI connected' : 'CLI disconnected'}>
                      <StyledActivityIndicator active={is_cli_active} />
                    </Tooltip>
                  )}
                </Div>
              </NavLink>
            ))}
          </StyledNavLinks>
          {filtered_views.length === 0 && (
            <Text muted p={{ x: 2, y: 1.5 }}>
              No views found...
            </Text>
          )}
        </StyledCompactViewsScrollContainer>
      </>
    );
  }

  return (
    <StyledNavLinks>
      {include_all && (
        <NavLink to={`/${type}`} isActive={() => pathname === `/${type}`}>
          <Div
            p={compact ? { x: 1 } : { x: 0.5 }}
            w={100}
            flex={{ align: 'center', justify: 'space-between' }}>
            <Text semi ellipsis>
              All
            </Text>
          </Div>
        </NavLink>
      )}
      {displayed_views.map((view) => (
        <NavLink key={view.id} to={getViewPath(view)}>
          <Div
            p={compact ? { x: 1 } : { x: 0.5 }}
            w={100}
            flex={{ align: 'center', justify: 'space-between' }}>
            <Text semi ellipsis>
              {view.name}
            </Text>
            {view?.id === 'cli' && (
              <Tooltip
                align="right"
                offset={[-8, 12]}
                tooltip={is_cli_active ? 'CLI connected' : 'CLI disconnected'}>
                <StyledActivityIndicator active={is_cli_active} />
              </Tooltip>
            )}
          </Div>
        </NavLink>
      ))}
      {displayed_views.length < all_views.length && (
        <DropdownMenu
          placement="right-start"
          block
          p={0}
          search
          search_placeholder="Find a saved view..."
          parent_width={{ min: 248, max: 248 }}
          renderToggle={(opened, toggle) => (
            <StyledNavLinks $hover_background={compact ? 'neutral' : undefined}>
              <ClickableArea
                highlight={opened}
                on={compact ? undefined : 'background'}
                onClick={() => toggle(!opened)}>
                <Div p={compact ? { x: 1 } : { x: 0.5 }} w={100}>
                  <Text semi ellipsis muted={!opened}>
                    See all {views?.models.length} views...
                  </Text>
                </Div>
              </ClickableArea>
            </StyledNavLinks>
          )}
          options={all_views.map((view) => ({
            label: view.name,
            value: view.id,
            to: getViewPath(view),
          }))}
        />
      )}
    </StyledNavLinks>
  );
};

const GetStartedNavLink = styled(StyledNavLinks)`
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const GetStartedCloseButton = ({
  hide = true,
  onClick,
}: {
  hide?: boolean;
  onClick?: MouseEventHandler<HTMLElement>;
}) => {
  return (
    <StyledGetStartedCloseButton hide={hide}>
      <Tooltip tooltip="Dismiss tab" align="right" offset={[-7, 12]}>
        <Link small neutral icon="close" onClick={onClick} />
      </Tooltip>
    </StyledGetStartedCloseButton>
  );
};

const StyledGetStartedCloseButton = styled.div<{ hide: boolean }>`
  visibility: ${({ hide }) => (hide ? 'hidden' : 'visible')};
  position: absolute;
  right: ${({ theme }) => theme.spacing(1.5)};
  top: ${({ theme }) => theme.spacing(0)};
  bottom: ${({ theme }) => theme.spacing(0)};
  display: flex;

  ${GetStartedNavLink}:hover & {
    visibility: visible;
    width: auto;
  }
`;

const GetStartedBadge = styled(Badge)`
  visibility: visible;
  position: absolute;
  right: ${({ theme }) => theme.spacing(1)};

  ${GetStartedNavLink}:hover & {
    visibility: hidden;
  }
`;

const StyledHideNotSelected = styled.div<{ hide: boolean }>`
  ${StyledNavLinks} {
    & > a,
    button {
      ${({ hide }) =>
        hide &&
        css`
          &:not(.active) {
            display: none;
          }
        `}
    }
  }
`;

export const NavSections = ({
  title,
  collapsable = true,
  children,
  first = false,
}: {
  title: string;
  collapsable?: boolean;
  children: ReactNode;
  first?: boolean;
}) => {
  const { nav_collapsed } = useContext(DashboardContext);
  const [collapsed, setCollapsed] = useLocalStorage(
    `pref:nav-collapsed:${title.toLowerCase()}`,
    false,
  );

  if (nav_collapsed) {
    return <Div m={{ t: first ? 0 : 4 }}>{children}</Div>;
  }

  if (!collapsable) {
    return (
      <>
        <Text semi muted size="xs" m={{ x: 1.5, t: first ? 0 : 3, b: 1 }}>
          {title}
        </Text>
        {children}
      </>
    );
  }
  return (
    <>
      <Link
        no_underline
        onClick={() => setCollapsed(!collapsed)}
        flex={{ align: 'center' }}
        m={{ x: 1.5, t: first ? 0 : 3, b: 1 }}
        neutral
        icon={collapsed ? 'arrow_right' : 'arrow_drop_down'}
        reveal_icon_on_hover={!collapsed}>
        <Text semi size="xs">
          {title}
        </Text>
      </Link>
      <StyledHideNotSelected hide={collapsed}>{children}</StyledHideNotSelected>
    </>
  );
};

const DashboardNav = () => {
  const {
    organization,
    team,
    get_started_pending_steps,
    show_get_started,
    setShowGetStarted,
    nav_collapsed,
  } = useContext(DashboardContext);
  const { HookdeckAPI } = useContext(GlobalContext);
  const { pathname } = useLocation();
  const { query } = useSearchQuery<{
    date?: DateRange;
  }>();

  const { data: event_views } = useSWR(APIMethodKeys.views.list({ type: 'events' }), () =>
    HookdeckAPI.views.list({ type: 'events' }),
  );
  const { data: request_views } = useSWR(APIMethodKeys.views.list({ type: 'requests' }), () =>
    HookdeckAPI.views.list({ type: 'requests' }),
  );

  const { data: issues_count_response } = useSWR(
    APIMethodKeys.issues.count({ status: 'OPENED' }),
    () => HookdeckAPI.issues.count({ status: 'OPENED' }),
    {
      refreshInterval: 1000 * 60,
    },
  );

  const history = useHistory();

  const issues_count = issues_count_response?.count || 0;

  const closeGetStarted = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    setShowGetStarted(false);
    if (pathname === '/get-started') {
      history.push('/');
    }
  };

  const collapsedInspectFlyout = (type: 'events' | 'requests', toggle: (show: boolean) => void) => (
    <Div min_w={{ px: 248 }}>
      <ViewsContextProvider type={type} redirect_root={`/${type}`}>
        <Views type={type} compact toggle={toggle} />
      </ViewsContextProvider>
    </Div>
  );

  const collapsedInspectDropdown = (link: (typeof inspect_sections)[number]) => (
    <Dropdown
      key={link.key}
      portal
      on_hover
      placement="right-start"
      renderToggle={(opened, toggle) => (
        <StyledCollapsedLinkWrapper>
          <NavLink
            to={link.path}
            aria-label={link.label}
            isActive={() => opened || pathname.startsWith(link.path)}
            onClick={() => toggle(false)}>
            <Icon left icon={link.icon} pointer p={0.5} />
          </NavLink>
        </StyledCollapsedLinkWrapper>
      )}>
      {(toggle) => collapsedInspectFlyout(link.key as 'events' | 'requests', toggle)}
    </Dropdown>
  );

  const navIcon = (icon: IconName, label: string, dot_color?: 'danger' | 'warning') => {
    const icon_element = <CollapsedNavIcon icon={icon} dot_color={dot_color} left />;

    if (!nav_collapsed) {
      return <Icon left icon={icon} pointer p={0.5} />;
    }

    return (
      <Tooltip tooltip={label} side="right" align="center" offset={[0, 12]}>
        {icon_element}
      </Tooltip>
    );
  };

  const project_picker_toggle = (opened: boolean, toggle: (opened: boolean) => void) => (
    <ClickableArea
      on="background"
      rounded
      highlight={opened}
      flex={{ align: 'center' }}
      onClick={() => toggle(!opened)}
      p={1}>
      <Avatar name={organization!.name} small />
      {!nav_collapsed && (
        <Text ellipsis text_wrap={false} semi min_w={{ px: 0 }} p={{ l: 2, r: 1 }}>
          {team!.name}
        </Text>
      )}
      {!nav_collapsed && <Icon muted icon="expand_all" m={{ l: 'auto' }} pointer />}
    </ClickableArea>
  );

  return (
    <DashboardNavShell>
      {nav_collapsed ? (
        <Div m={{ y: 3 }} flex={{ justify: 'center' }}>
          <Dropdown
            placement="right-start"
            p={0}
            renderToggle={(opened, toggle) => (
              <Tooltip tooltip={team!.name} side="right" align="center" offset={[0, 8]}>
                {project_picker_toggle(opened, toggle)}
              </Tooltip>
            )}>
            <TeamSelection />
          </Dropdown>
        </Div>
      ) : (
        <Div m={{ x: 2, y: 3 }} flex={{ align: 'center', justify: 'center' }}>
          <Div style={{ minWidth: 0, flex: '1 1 auto' }}>
            <Dropdown
              block
              placement="bottom-start"
              p={0}
              renderToggle={(opened, toggle) => project_picker_toggle(opened, toggle)}>
              <TeamSelection />
            </Dropdown>
          </Div>
        </Div>
      )}
      <Divider />
      <ScrollContainer p={{ x: 2, y: 3 }}>
        {show_get_started !== false && (
          <GetStartedNavLink>
            <NavLink to="/get-started">
              {navIcon('grid_view', 'Get Started')}
              {!nav_collapsed && (
                <>
                  <Text semi size="m" w={100} flex={{ justify: 'space-between' }}>
                    Get Started
                  </Text>
                  {pathname === '/get-started' ? (
                    <GetStartedCloseButton hide={false} onClick={closeGetStarted} />
                  ) : (
                    <>
                      {get_started_pending_steps > 0 && (
                        <GetStartedBadge subtle>{get_started_pending_steps}</GetStartedBadge>
                      )}
                      <GetStartedCloseButton onClick={closeGetStarted} />
                    </>
                  )}
                </>
              )}
            </NavLink>
          </GetStartedNavLink>
        )}
        <NavSections title="Configure" first>
          <StyledNavLinks>
            {configure_sections.map((link) => (
              <NavLink
                key={link.path}
                to={link.path}
                aria-label={nav_collapsed ? link.label : undefined}>
                {navIcon(link.icon, link.label)}
                {!nav_collapsed && (
                  <Text semi size="m">
                    {link.label}
                  </Text>
                )}
              </NavLink>
            ))}
          </StyledNavLinks>
        </NavSections>
        <NavSections title="Monitor">
          <StyledNavLinks>
            <NavLink to="/issues" aria-label={nav_collapsed ? 'Issues' : undefined}>
              {navIcon('issues', 'Issues', issues_count > 0 ? 'danger' : undefined)}
              {!nav_collapsed && (
                <Text semi size="m" w={100} flex={{ justify: 'space-between' }}>
                  Issues
                  {issues_count > 0 && (
                    <Badge danger subtle>
                      {issues_count || 1}
                    </Badge>
                  )}
                </Text>
              )}
            </NavLink>
            <NavLink to="/issue-triggers" aria-label={nav_collapsed ? 'Issue Triggers' : undefined}>
              {navIcon('issue_trigger', 'Issue Triggers')}
              {!nav_collapsed && (
                <Text semi size="m" w={100} flex={{ justify: 'space-between' }}>
                  Issue Triggers
                </Text>
              )}
            </NavLink>
            <NavLink
              to={`/metrics${
                pathname.includes('metrics') && !isEmpty(query?.date)
                  ? `?${stringifyQuery({ date: query.date })}`
                  : ''
              }`}
              aria-label={nav_collapsed ? 'Metrics' : undefined}
              isActive={() => pathname.startsWith('/metrics')}>
              {navIcon('metrics', 'Metrics')}
              {!nav_collapsed && (
                <Text semi size="m" w={100} flex={{ justify: 'space-between' }}>
                  Metrics
                </Text>
              )}
            </NavLink>
          </StyledNavLinks>
        </NavSections>
        <NavSections title="Inspect">
          <StyledNavLinks>
            {inspect_sections.map((link) =>
              nav_collapsed && (link.key === 'requests' || link.key === 'events') ? (
                collapsedInspectDropdown(link)
              ) : (
                <Fragment key={link.key}>
                  <NavLink
                    to={link.path}
                    aria-label={nav_collapsed ? link.label : undefined}
                    isActive={() => pathname.startsWith(link.path)}>
                    {navIcon(link.icon, link.label)}
                    {!nav_collapsed && (
                      <Text semi size="m" ellipsis>
                        {link.label}
                      </Text>
                    )}
                  </NavLink>
                  {!nav_collapsed &&
                    request_views &&
                    link.key === 'requests' &&
                    request_views?.count > 0 && (
                      <ViewsContextProvider type="requests" redirect_root="/requests">
                        <Route path="/requests">
                          <StyledViewsContainer flex={{ direction: 'column' }}>
                            <Route path="/requests">
                              <Div m={{ l: 6.5 }} flex={{ direction: 'column' }}>
                                <Views type="requests" />
                              </Div>
                            </Route>
                          </StyledViewsContainer>
                        </Route>
                      </ViewsContextProvider>
                    )}
                  {!nav_collapsed &&
                    event_views &&
                    link.key === 'events' &&
                    event_views?.count > 0 && (
                      <ViewsContextProvider type="events" redirect_root="/events">
                        <Route path="/events">
                          <StyledViewsContainer flex={{ direction: 'column' }}>
                            <Route path="/events">
                              <Div m={{ l: 6.5 }} flex={{ direction: 'column' }}>
                                <Views type="events" />
                              </Div>
                            </Route>
                          </StyledViewsContainer>
                        </Route>
                      </ViewsContextProvider>
                    )}
                </Fragment>
              ),
            )}
          </StyledNavLinks>
        </NavSections>
      </ScrollContainer>
      <NavFooter />
    </DashboardNavShell>
  );
};

export default DashboardNav;
